import { Divider } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import { Skeleton } from "../Skeleton";
import { TipCallout } from "../TipCallout";

const HEADER_MIN_HEIGHT = 72;
const HEADER_ICON_SIZE = 40;
const TITLE_LINE_HEIGHT = 24;
const LABEL_LINE_HEIGHT = 16;
const ROW_ICON_SIZE = 24;
const ROW_TITLE_LINE_HEIGHT = 20;
const ROW_TITLE_WIDTHS = ["45%", "35%", "40%"] as const;
const CHEVRON_SIZE = 24;
const LEVEL_CHIP_HEIGHT = 28;
const LEVEL_CHIP_WIDTH = 84;
const ROW_TRAILING_GAP = 4;

const TextLine = ({
	lineHeight,
	barHeight,
	width,
}: {
	lineHeight: number;
	barHeight: number;
	width: number | `${number}%`;
}) => (
	<Box height={lineHeight} justifyContent="center">
		<Skeleton width={width} height={barHeight} />
	</Box>
);

const LevelChipSkeleton = () => {
	const theme = useAppTheme();

	return (
		<Skeleton
			height={LEVEL_CHIP_HEIGHT}
			width={LEVEL_CHIP_WIDTH}
			borderRadius={theme.roundness}
		/>
	);
};

const ItemTypesCardSkeleton = ({ numRows }: { numRows: number }) => {
	const theme = useAppTheme();

	return (
		<FlatCard style={{ overflow: "hidden" }}>
			<Box
				flexDirection="row"
				alignItems="center"
				minHeight={HEADER_MIN_HEIGHT}
				paddingHorizontal={16}
				gap={16}
			>
				<Skeleton size={HEADER_ICON_SIZE} variant="circle" />
				<Box flex={1} gap={2}>
					<TextLine lineHeight={TITLE_LINE_HEIGHT} barHeight={16} width="60%" />
					<TextLine lineHeight={LABEL_LINE_HEIGHT} barHeight={12} width="40%" />
				</Box>
			</Box>
			<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />
			{Array.from({ length: numRows }).map((_, rowIndex) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
				<Box key={rowIndex}>
					{rowIndex > 0 ? (
						<Divider
							style={{
								marginHorizontal: 16,
								backgroundColor: theme.colors.surfaceVariant,
							}}
						/>
					) : null}
					<Box
						flexDirection="row"
						alignItems="center"
						gap={8}
						paddingVertical={8}
						paddingHorizontal={16}
					>
						<Box
							width={32}
							height={32}
							alignItems="center"
							justifyContent="center"
						>
							<Skeleton size={ROW_ICON_SIZE} variant="circle" />
						</Box>
						<Box flex={1} justifyContent="center">
							<TextLine
								lineHeight={ROW_TITLE_LINE_HEIGHT}
								barHeight={14}
								width={ROW_TITLE_WIDTHS[rowIndex % ROW_TITLE_WIDTHS.length]}
							/>
						</Box>
						<Box flexDirection="row" alignItems="center" gap={ROW_TRAILING_GAP}>
							<LevelChipSkeleton />
							<Skeleton size={CHEVRON_SIZE} variant="circle" />
						</Box>
					</Box>
				</Box>
			))}
			<Box paddingTop={8} paddingHorizontal={16} paddingBottom={16}>
				<TipCallout isLoading />
			</Box>
		</FlatCard>
	);
};

export const ItemTypesSectionSkeleton = ({
	numCards = 2,
	numRows = 3,
}: { numCards?: number; numRows?: number }) => (
	<Box gap={12}>
		{[...new Array(numCards).keys()].map((card) => (
			<ItemTypesCardSkeleton key={card} numRows={numRows} />
		))}
	</Box>
);
